import { useEffect, useReducer } from "react"
import { Button } from "@mui/material"
import { useLocation, useParams } from "react-router-dom"
import { navigate } from "lib/routes/navigate"
import { useAsync } from "lib/@hooks/useAsync"
import { useRefreshOnInvalidation } from "lib/graphql/use-refresh-on-invalidation"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { Methods, useScheduleByMethodId } from "routes/schedule/compare/use-schedule-by-method-id"
import { ComparisonView } from "routes/schedule/compare/comparisonView"
import { CompareEmptyState } from "routes/schedule/compare/compare-empty-state"
import { getCompareDestinationId } from "routes/schedule/compare/lib/get-compare-destination-id"
import { getPreviousScheduleVersion } from "routes/timeline/controller/previous-schedule-version"

export function CompareSchedules() {
    const { destination, source } = useParams()
    const { search } = useLocation()
    const [attempt, retry] = useReducer(increment, 0)
    const refresh = useRefreshOnInvalidation("editSchedule", "rawSchedule", "draftHistoryEntry")
    const scheduleDestination = useScheduleByMethodId(destination, refresh.id)
    const scheduleSource = useScheduleByMethodId(source, refresh.id)
    const selfCompare = getSelfCompare()
    const lookupKey = selfCompare && `${destination}/${source}@${selfCompare.version}#${attempt}`
    const lookup = useAsync(lookupPreviousVersion, null, lookupKey ?? "none")
    const lookupDone = !!lookupKey && lookup?.key === lookupKey
    const previousVersion = lookupDone ? lookup.version : null
    const selfCompareId = selfCompare?.id
    useEffect(redirectToPreviousVersion, [selfCompareId, previousVersion, destination, search])

    if (scheduleDestination === IS_LOADING || scheduleSource === IS_LOADING) return null
    // A loader returns null when its schedule or version doesn't exist
    if (scheduleDestination === null || scheduleSource === null) {
        return (
            <CompareEmptyState
                id={getCompareDestinationId(destination)}
                title="This comparison is no longer available"
                detail="One of the schedules or versions being compared no longer exists."
            />
        )
    }
    if (lookupDone && lookup.failed) {
        return (
            <CompareEmptyState
                id={selfCompareId}
                title="Couldn't load an earlier version to compare"
                detail="Something went wrong looking up the previous version of this schedule."
            >
                <Button sx={{ mt: 2 }} variant="outlined" onClick={retry} data-cy="compare-retry">
                    Try again
                </Button>
            </CompareEmptyState>
        )
    }
    if (lookupDone && !previousVersion) {
        return (
            <CompareEmptyState
                id={selfCompareId}
                title="No earlier version to compare"
                detail="This is the earliest version we hold for this schedule."
            />
        )
    }
    if (selfCompare) return null

    return <ComparisonView $lazy key={refresh.id} destination={scheduleDestination} source={scheduleSource} />

    function getSelfCompare() {
        const [destinationDefinition] = asArray(scheduleDestination)
        const [sourceDefinition] = asArray(scheduleSource)
        if (!isLoadedFor(destination, destinationDefinition) || !isLoadedFor(source, sourceDefinition)) return null
        if (String(destinationDefinition.id) !== String(sourceDefinition.id)) return null
        const version = Number(destinationDefinition.version)
        if (!Number.isFinite(version) || version !== Number(sourceDefinition.version)) return null
        return { id: destinationDefinition.id, version }
    }

    async function lookupPreviousVersion() {
        if (!lookupKey) return null
        try {
            const version = await getPreviousScheduleVersion({
                id: String(selfCompare.id),
                version: selfCompare.version,
            })
            return { key: lookupKey, version, failed: version === undefined }
        } catch (error) {
            console.error(error)
            return { key: lookupKey, failed: true }
        }
    }

    function redirectToPreviousVersion() {
        if (!selfCompareId || !previousVersion) return
        navigate(
            `/app/schedules/compare/${destination}/${Methods.scheduleHistory}!${selfCompareId}-${previousVersion}${search}`,
            { replace: true }
        )
    }
}

function isLoadedFor(idWithMethod, definition) {
    if (!definition?.id) return false
    const [method, id] = idWithMethod?.split("!") ?? []
    if (method === Methods.schedule) return String(definition.id) === id
    if (method === Methods.scheduleHistory) return `${definition.id}-${definition.version}` === id
    return false
}

function asArray(value) {
    return Array.isArray(value) ? value : []
}

function increment(value) {
    return value + 1
}
